import styled from '@emotion/styled';
import { forwardRef, ReactNode } from 'react';
import { globalPadding } from './Layout/Layout';
import { mainBg } from './styles/Main';
import { deepMerge, useResponsiveUrl } from './utils';
import { BreakPointsOnly } from './theme';

export const ratio = {
  xs: 1 / 1.4,
  md: 1 / 1.6,
};

export const containerOffset: BreakPointsOnly = {
  xs: `${(64 - 24) / 16}rem`,
  lg: `-${(64 + 24) / 16}rem`,
  xl: `-${140 / 16}rem`,
};

export const contentBottom: BreakPointsOnly = {
  lg: '50%',
  md: '0',
};

export const contentMargin: BreakPointsOnly = {
  sm: '-16rem',
  md: '0',
};

export const Container = styled.section<{ customContentOffset?: BreakPointsOnly }>(({ theme, customContentOffset = containerOffset }) => {
  const styles = {
    color: theme.palette.foreground[900][0],
    backgroundColor: theme.palette.background[mainBg][0],
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    [theme.breakpoints.up('md')]: {
      alignItems: 'flex-end',
    },
  };

  const responsiveOffset = theme.mixins.create(customContentOffset, (p) => ({ marginBottom: p }));

  return deepMerge(styles, responsiveOffset);
});

export  const Content = styled.div<{
  customContentOffset?: BreakPointsOnly,
  responsiveMargin?: BreakPointsOnly
}>(({ theme, customContentOffset = contentBottom,
  responsiveMargin = contentMargin }) => {
  const styles = {
    width: '100%',
    marginTop: '-1.75rem',
    position: 'relative',
    zIndex: 2,
    padding: '0 1rem',
    '@media (min-width: 600px)': { padding: '0 2rem' },
    '@media (min-width: 960px)': { padding: '0 3.5rem' },
    '@media (min-width: 1920px)': { padding: '0 8rem' },
    [theme.breakpoints.up('md')]: {
      position: 'absolute',
      right: 0,
    },
    [theme.breakpoints.up('lg')]: {
      transform: 'translateY(50%)',
    },
  };

  const responsivePadding = theme.mixins.create(globalPadding, (p) => ({
    padding: `0 ${theme.spacings[p]}rem`,
  }));

  const responsiveBottom = theme.mixins.create(customContentOffset, (p) => ({ bottom: p }));
  const responsiveMarginTop = theme.mixins.create(responsiveMargin, (p) => ({ marginTop: p }));

  return deepMerge(styles, responsivePadding, responsiveBottom, responsiveMarginTop);
});

export  const CoverWrapper = styled.div(({ theme }) => ({
  height: '100%',
  background: 'no-repeat center url(/static/images/placeholder/cover-single.svg)',
  backgroundSize: 'cover',
  [theme.breakpoints.up('md')]: {
    width: '85%',
  },
  [theme.breakpoints.up('lg')]: {
    width: '74%',
  },
}));

export const CoverImage = styled.div(({ theme }) => ({
  width: '100%',
  paddingTop: `${ratio.xs * 100}%`,
  position: 'relative',
  zIndex: 0,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  [theme.breakpoints.up('md')]: {
    paddingTop: `${ratio.md * 100}%`,
    top: 0,
    left: 0,
  },
}));

export const GradientY = styled.div(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    background: `linear-gradient(to left, ${theme.palette.background[mainBg][0]}, rgba(0,0,0,0))`,
    height: '100%',
    width: '60%',
    position: 'absolute',
    bottom: 0,
    right: -1,
    zIndex: 1,
  },
}));

export const GradientX = styled.div(({ theme }) => ({
  background: `linear-gradient(to top, ${theme.palette.background[mainBg][0]}, rgba(0,0,0,0))`,
  position: 'absolute',
  bottom: -1,
  width: '100%',
  height: '31%',
  zIndex: 1,
}));

export const CoverOverlay = styled.div(({ theme }) => {
  const styles = {
    width: '100%',
    position: 'absolute',
    bottom: 'calc(100% + 1.5rem)',
    right: 0,
    [theme.breakpoints.up('sm')]: {
      width: 'max-content',
    },
    [theme.breakpoints.up('md')]: {
      zIndex: 3,
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 'auto',
      top: 'auto',
    },
  };

  const responsivePadding = theme.mixins.create(globalPadding, (p) => ({
    padding: `0 ${theme.spacings[p]}rem`,
  }));

  return deepMerge(styles, responsivePadding);
});

export type HeaderCoverProps = {
  image?: string; children: ReactNode; overlay?: ReactNode, containerOffset?: BreakPointsOnly,
  contentOffset?: BreakPointsOnly
  responsiveMargin?: BreakPointsOnly
};

const HeaderCover = forwardRef<HTMLDivElement, HeaderCoverProps>(function HeaderCover({ image, children, overlay,
  containerOffset, contentOffset, responsiveMargin }, ref) {
  const imgUrl = useResponsiveUrl({ url: image, desktop: 1920, mobile: 880 });

  return (
    <Container className="header-cover" ref={ref} customContentOffset={containerOffset} >
      <CoverWrapper>
        <CoverImage style={{ backgroundImage: `url(${imgUrl})` }}>
          <GradientX />
          <GradientY />
        </CoverImage>
      </CoverWrapper>
      <Content customContentOffset={contentOffset}
        responsiveMargin={responsiveMargin}>
        {overlay && <CoverOverlay>{overlay}</CoverOverlay>}
        {children}
      </Content>
    </Container>
  );
});

export default HeaderCover;
